import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Button from '../../../../components/Button/Button'
import CardInfo from '../../../../components/CardInfo/CardInfo'
import ContentLayout from '../../../../components/ContentLayout/ContentLayout'
import MainHeading from '../../../../components/MainHeading/MainHeading'
import TimeLineSteps from '../../../../components/TimeLineSteps/TimeLineSteps'
import { formatRequestsListTableCheckDuplicates } from '../../../../helpers/tableUtils'
import {
    sendEventToIntercom,
    tagHotjarRecording,
} from '../../../../helpers/util'
import useAlert from '../../../../hooks/useAlert'
import useAuth from '../../../../hooks/useAuth'
import useFilterModal from '../../../../hooks/useFilterModal'
import useFleet from '../../../../hooks/useFleet'
import useGlobalLoading from '../../../../hooks/useGlobalLoading'
import useRequest from '../../../../hooks/useRequest'
import FleetTable from '../../../FleetPage/components/FleetTable'
import RequestTable from '../../../RequestsPage/RequestTable'
import PropTypes from 'prop-types'
import { logErrorOnServer } from '../../../../helpers/serverLogging'
import AppRoutes from '../../../../routes/appRoutes'
// import { parse } from 'qs'
import CreatePartsQuoteForm from './components/CreatePartsQuoteForm'
import { createPartsRequestSteps } from '../../../../helpers/createRequestutil'
import useScreen from '../../../../hooks/useScreen'
import WarningCard from '../WarningCard'
import { getFleetById } from '../../../../../../indexedDb/fleets'
import { handleAssetUpdate } from '../Repair/utils/assetUtils'
import { handleNextStep } from '../Repair/utils/updateStepState'
import { useNetworkStatus } from '../../../../utils/networkStatus'
import { onGettedAssetLocationInfo } from '../../../../utils/onGettedAssetLocationInfo'
import { getRequestsByType } from '../../../../../../indexedDb/requests'
import { savePendingRequest } from '../../../../../../indexedDb/pendingRequests'
import {
    handleOnCancel,
    handleOnDone,
    removeStepsAndRequests,
} from '../../../../utils/handleOnDone'
import { processImagesHelper } from '../../../../utils/processImagesHelper'
import { trackPartsRequestCreatedOnline } from '../../../../utils/sendGAAnalyticsEvent'
const logError = logErrorOnServer('New Repair Request')

const CreatePartsRequestFlow = ({ history }) => {
    const [steps, setSteps] = useState(() => {
        const savedState = localStorage.getItem('partsRequestState')
        if (savedState) {
            const { steps: savedSteps } = JSON.parse(savedState)
            return savedSteps || createPartsRequestSteps
        } else {
            return createPartsRequestSteps
        }
    })
    const [selectedAsset, setSelectedAsset] = useState([])
    const [changedStep, setChangedStep] = useState(false)
    const [error, setError] = useState(null)
    const [listOfRequests, setListOfRequests] = useState([])
    const { user } = useAuth()
    const {
        saveFilters,
        handleCreatePartsRequest,
        changeNeedsUpdate,
        needsUpdateDashboard,
    } = useRequest()
    const { handleGetAsset } = useFleet()
    const { changeLoading } = useGlobalLoading()
    const { handleChangeAlert, handleChangeOpen } = useAlert()
    const formRef = useRef()
    const [successfulSubmit, setSuccessfulSubmit] = useState(false)
    const [resetTouch, setResetTouch] = useState(false)
    const [assetInfo, setAssetInfo] = useState({})
    const [images, setImages] = useState([])
    const [photosIds, setPhotosIds] = useState([])
    const [imageRetryAttempted, setImageRetryAttempted] = useState(false)
    const [hasUnsyncedData, setHasUnsyncedData] = useState(true)
    const [isValidForm, setIsValidForm] = useState(false)
    const { isMobile, isTablet } = useScreen()
    const { isOffline } = useNetworkStatus()
    const customAssetID = selectedAsset && selectedAsset[0]?.customId

    // Leaving the equipmentId just in case that is needed after
    // const equipmentId = parse(window.location.search, {
    //     ignoreQueryPrefix: true,
    // })?.equipmentId

    const location = useLocation()
    let assetPassed = location.state?.asset
    const [asset, setAsset] = useState(assetPassed)

    const {
        filteredData: filteredOpenRequests,
        onFilter: onFilterOpenRequests,
        setFilteredData: setFilterOpenRequests,
    } = useFilterModal(
        formatRequestsListTableCheckDuplicates,
        listOfRequests,
        saveFilters,
        selectedAsset[0],
    )

    const saveStateToStorage = (steps, assetInfo) => {
        // Ensure customId is set
        assetInfo.customId = customAssetID

        // Make sure we preserve all properties needed for the form
        // If some fields are missing from assetInfo but available in selectedAsset
        if (selectedAsset && selectedAsset[0]) {
            const selectedAssetData = selectedAsset[0]

            // Preserve essential fields for make/model display
            assetInfo.manufacturer =
                assetInfo.manufacturer || selectedAssetData.manufacturer
            assetInfo.simpleType =
                assetInfo.simpleType || selectedAssetData.simpleType
            assetInfo.model = assetInfo.model || selectedAssetData.model
            assetInfo.name = assetInfo.name || selectedAssetData.name
            assetInfo.customerAlias =
                assetInfo.customerAlias ||
                selectedAssetData.customerAlias ||
                selectedAssetData.customer_alias

            // Ensure salesforceID is preserved
            assetInfo.salesforceID =
                assetInfo.salesforceID ||
                selectedAssetData.salesforceID ||
                selectedAssetData.salesforceId ||
                selectedAssetData.external_id
        }

        const currentState = { steps, assetInfo }
        localStorage.setItem('partsRequestState', JSON.stringify(currentState))
    }

    const getStoredActiveStepName = storedSteps =>
        storedSteps?.find(step => step?.active)?.name

    const getStoredAssetId = storedAsset =>
        storedAsset?.salesforceID ||
        storedAsset?.salesforceId ||
        storedAsset?.external_id ||
        null

    const loadStateFromStorage = () => {
        const savedState = localStorage.getItem('partsRequestState')
        if (savedState) {
            const parsedState = JSON.parse(savedState)
            const parsedSteps = parsedState?.steps || []
            const parsedAssetInfo = parsedState?.assetInfo || {}
            const parsedAsset = Array.isArray(parsedAssetInfo)
                ? parsedAssetInfo[0]
                : parsedAssetInfo
            const activeStepName = getStoredActiveStepName(parsedSteps)
            const hasValidStoredAsset = Boolean(getStoredAssetId(parsedAsset))

            setSteps(parsedSteps)

            // On reload while the Select Asset step is active, don't restore a
            // previously-selected asset – the user should pick fresh.
            if (activeStepName === 'Select Asset' || !hasValidStoredAsset) {
                setAssetInfo({})
                setSelectedAsset([])
                return
            }

            setAssetInfo(parsedAsset)
            setSelectedAsset([parsedAsset])
        }
    }

    const handleSyncState = async () => {
        try {
            const savedState = localStorage.getItem('partsRequestState')
            const parsedState = savedState ? JSON.parse(savedState) : {}
            const saveAssetInfo = parsedState?.assetInfo
            const parsedAsset = Array.isArray(saveAssetInfo)
                ? saveAssetInfo[0]
                : saveAssetInfo
            const activeStepName = getStoredActiveStepName(parsedState?.steps)
            const storedAssetId = getStoredAssetId(parsedAsset)

            // When on the Select Asset step (or no valid stored asset), there is
            // nothing to sync – avoid firing an erroneous item API call.
            if (activeStepName === 'Select Asset' || !storedAssetId) {
                setHasUnsyncedData(false)
                return
            }

            if (parsedAsset) {
                changeLoading(true)

                const paramsToGetFleetAsset = {
                    assetId: storedAssetId,
                    companyId: user?.company?.id,
                    getRequests: true,
                }

                handleGetAsset(
                    paramsToGetFleetAsset,
                    response => {
                        const locationInfo = onGettedAssetLocationInfo(response)

                        let updatedAssetInfo = {
                            ...parsedAsset,
                            serviceLocationId:
                                parsedAsset?.serviceLocation?.external_id ||
                                locationInfo.serviceLocationId,
                            model: response?.data?.asset?.model,
                            isCreatedOffline: isOffline,
                        }

                        if (response?.data?.asset) {
                            updatedAssetInfo.manufacturer =
                                response.data.asset.manufacturer ||
                                updatedAssetInfo.manufacturer
                            updatedAssetInfo.simpleType =
                                response.data.asset.simpleType ||
                                updatedAssetInfo.simpleType
                            updatedAssetInfo.salesforceID =
                                response.data.asset.salesforceID ||
                                response.data.asset.id ||
                                updatedAssetInfo.salesforceID
                        }

                        updatedAssetInfo = enrichAssetData(updatedAssetInfo)

                        setSteps(parsedState.steps || [])
                        setAssetInfo(updatedAssetInfo)
                        changeLoading(false)
                    },
                    onGettedAssetError,
                )
            }

            setHasUnsyncedData(false)
        } catch (error) {
            console.error('Error syncing state:', error)
        }
    }

    useEffect(() => {
        const activeStep = steps?.find(f => f.active === true)
        if (asset && activeStep.name === 'Select Asset') {
            setSelectedAsset([asset])
        }
    }, [asset])

    useEffect(() => {
        const activeStep = steps?.find(f => f.active === true)
        if (
            asset &&
            selectedAsset[0] &&
            activeStep &&
            activeStep.name === 'Select Asset' &&
            !changedStep
        ) {
            setChangedStep(true)
            setTimeout(() => onNextStep(), 50)
            setAsset(null)
        }
    }, [selectedAsset, steps])

    useEffect(() => {
        loadStateFromStorage()
    }, [isOffline])

    useEffect(() => {
        if (isOffline) {
            saveStateToStorage(steps, assetInfo)
            setHasUnsyncedData(true)
        }
    }, [isOffline, steps, assetInfo])

    useEffect(() => {
        if (!isOffline && hasUnsyncedData) {
            handleSyncState()
        }
    }, [isOffline, hasUnsyncedData])

    useEffect(() => {
        saveStateToStorage(steps, assetInfo)
    }, [steps, assetInfo])

    useEffect(() => {
        if (listOfRequests?.length > 0) {
            setFilterOpenRequests(
                formatRequestsListTableCheckDuplicates(
                    listOfRequests,
                    selectedAsset[0],
                ),
            )
        }

        return () => {
            setFilterOpenRequests([])
        }
    }, [listOfRequests])

    // Leaving in just in case
    // useEffect(() => {
    //     if (equipmentId) {
    //         handleGetAssetInfo(equipmentId)
    //     }
    // }, [])

    useEffect(() => {
        if (selectedAsset?.length === 1) {
            setError(null)
        }
    }, [selectedAsset])

    const onGettedAssetSuccess = response => {
        let asset = response?.data?.asset
        const assetRequests = asset?.openRequests

        // Ensure asset data is complete
        asset = enrichAssetData(asset)

        // Make sure salesforceID is explicitly set
        if (asset && response?.data?.asset) {
            asset.salesforceID =
                response.data.asset.salesforceID ||
                response.data.asset.id ||
                response.data.asset.external_id ||
                asset.salesforceID
        }

        const handlers = {
            setListOfRequests,
            setAssetInfo,
            changeLoading,
        }

        handleAssetUpdate(asset, assetRequests, handlers)
    }

    // Helper function to ensure asset data completeness
    const enrichAssetData = assetData => {
        if (!assetData) return {}

        // Make a copy to avoid mutation issues
        let enrichedAsset = { ...assetData }

        // If we already have data in selectedAsset, use it to fill in gaps
        if (selectedAsset && selectedAsset.length > 0) {
            const existingData = selectedAsset[0]

            // Preserve critical fields for the form's make/model display
            enrichedAsset.manufacturer =
                enrichedAsset.manufacturer || existingData.manufacturer
            enrichedAsset.simpleType =
                enrichedAsset.simpleType || existingData.simpleType
            enrichedAsset.model = enrichedAsset.model || existingData.model
            enrichedAsset.customerAlias =
                enrichedAsset.customerAlias ||
                existingData.customerAlias ||
                existingData.customer_alias

            // Ensure salesforceID is preserved
            enrichedAsset.salesforceID =
                enrichedAsset.salesforceID ||
                existingData.salesforceID ||
                existingData.salesforceId ||
                existingData.external_id
        }

        return enrichedAsset
    }

    const onGettedAssetError = error => {
        tagHotjarRecording('API Error')
        const { message = 'Unable to load equipment.' } =
            error?.response?.data ?? {}
        console.log(message)
        changeLoading(false)
    }

    const handleGetAssetInfo = async customId => {
        if (asset) {
            return null
        }
        changeLoading(true)
        const selectedAssetId =
            customId ||
            selectedAsset[0]?.salesforceID ||
            selectedAsset[0]?.customId
        const paramsToGetFleetAsset = {
            assetId: selectedAssetId,
            companyId: user?.company?.id,
            getRequests: false,
        }
        if (isOffline) {
            const handlers = {
                setListOfRequests,
                setAssetInfo,
                changeLoading,
            }

            try {
                // Get the full asset data from IndexedDB
                let assetFromDB = await getFleetById(selectedAssetId)

                if (!assetFromDB || Object.keys(assetFromDB).length === 0) {
                    // If we can't find the asset by ID, use the selectedAsset data
                    const enrichedAsset = enrichAssetData(selectedAsset[0])

                    // Ensure salesforceID is explicitly preserved
                    if (selectedAsset[0]) {
                        enrichedAsset.salesforceID =
                            selectedAsset[0].salesforceID ||
                            selectedAsset[0].salesforceId ||
                            selectedAsset[0].external_id
                    }

                    setAssetInfo(enrichedAsset)
                    changeLoading(false)
                    return
                }

                // Ensure all asset fields are preserved
                assetFromDB = enrichAssetData(assetFromDB)

                // Explicitly ensure salesforceID is set
                assetFromDB.salesforceID =
                    assetFromDB.salesforceID ||
                    assetFromDB.salesforceId ||
                    assetFromDB.external_id ||
                    selectedAsset[0]?.salesforceID

                const storedOpenRequests = await getRequestsByType(
                    'openRequest',
                )
                const assetRequests = formatRequestsListTableCheckDuplicates(
                    storedOpenRequests,
                    assetFromDB,
                )

                // Call handleAssetUpdate with the enriched asset data
                handleAssetUpdate(assetFromDB, assetRequests, handlers)
            } catch (error) {
                // If there's an error, fallback to using enriched selectedAsset data
                const enrichedAsset = enrichAssetData(selectedAsset[0])

                // Ensure salesforceID is explicitly preserved
                if (selectedAsset[0]) {
                    enrichedAsset.salesforceID =
                        selectedAsset[0].salesforceID ||
                        selectedAsset[0].salesforceId ||
                        selectedAsset[0].external_id
                }

                setAssetInfo(enrichedAsset)
                changeLoading(false)
            }
        } else {
            handleGetAsset(
                paramsToGetFleetAsset,
                onGettedAssetSuccess,
                onGettedAssetError,
            )
        }
        tagHotjarRecording('Repair Request')
    }

    // Helper function to get asset name
    const getAssetName = () => {
        if (!selectedAsset || selectedAsset.length === 0) return ''
        const asset = selectedAsset[0]
        return (
            asset?.customer_alias ||
            asset?.customerAlias ||
            asset?.name ||
            'Unknown Asset'
        )
    }

    const onNextStep = () => {
        const activeStep = steps?.find(f => f.active === true)

        if (!activeStep) {
            return
        }

        const assetName = getAssetName()

        if (activeStep.name === 'Select Asset' && selectedAsset.length > 0) {
            // Update the step with description first to ensure it's shown
            const updatedSteps = steps.map(step => {
                if (step.name === 'Select Asset') {
                    return { ...step, description: assetName }
                }
                return step
            })

            setSteps(updatedSteps)

            // Then proceed with the transition using handleNextStep
            setTimeout(() => {
                handleNextStep({
                    steps: updatedSteps, // Use updated steps
                    setSteps,
                    setError,
                    selectedAsset,
                    handleGetAssetInfo,
                    handleChangeAlert,
                    formRef,
                    history,
                    handleChangeOpen,
                    currentStep: activeStep?.name,
                    newStep: 'Request Details',
                    newDescription: assetName, // Also pass as newDescription
                })
            }, 50)
            return
        }

        handleNextStep({
            steps,
            setSteps,
            setError,
            selectedAsset,
            handleGetAssetInfo,
            handleChangeAlert,
            formRef,
            history,
            handleChangeOpen,
        })
    }

    const onCreatedPartRequestSuccess = response => {
        const requestInfo = response?.data?.response?.request
        const createdWithoutImage = response?.data?.createdWithoutImage
        sendEventToIntercom('successful-parts-quote-request')
        setSuccessfulSubmit(true)
        setError(null)
        setImageRetryAttempted(false)
        changeLoading(false)
        trackPartsRequestCreatedOnline(response, user?.external_contact_id)
        changeNeedsUpdate({ dashboard: needsUpdateDashboard, request: true })
        handleChangeAlert({
            title: 'Request Created',
            message: createdWithoutImage
                ? 'Your parts request was created, but the image could not be processed and was not included.'
                : 'Check back to see the latest status',
            textOk: 'View Request',
            textCancel: 'Return to Dashboard',
            onCancel: () => handleOnCancel(history),
            onDone: () => {
                history?.push(
                    AppRoutes.viewPartsRequest(requestInfo?.salesforceId),
                )
                handleChangeOpen(false)
            },
            type: 'success',
        })
    }

    const onCreatedPartRequestError = error => {
        const {
            imageFailed,
            imageRetryAttempted: shouldRetryImage,
            message = 'Please check your internet connection and try again.',
        } = error?.response?.data ?? {}

        if (imageFailed && shouldRetryImage) {
            setImageRetryAttempted(true)
        }

        setError(message)
        setResetTouch(true)
        changeLoading(false)
        setFormSubmitting(false)
        logError(message)
    }

    const handleCommonSuccess = () => {
        setSuccessfulSubmit(true)
        setError(null)
        changeLoading(false)
        changeNeedsUpdate({ dashboard: needsUpdateDashboard, request: true })
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: 'serviceRequestFormSubmission' })
    }

    const onCreatedOfflineRequestSuccess = request => {
        handleCommonSuccess()
        handleChangeAlert({
            title: 'Your request is now pending',
            message:
                'Please reconnect this device within 72 hours to avoid delays in processing your request.',
            textCancel: 'Return to Dashboard',
            onCancel: () => handleOnCancel(history),
            textOk: 'View Request',
            onDone: () => {
                handleOnDone(
                    history,
                    request.id,
                    handleChangeOpen,
                    request.showType,
                )
            },
            type: 'question',
        })
    }

    const onCreateRequest = useCallback(
        async data => {
            changeLoading(true)

            // Check if assetInfo is incomplete and use selectedAsset as fallback
            let assetData = assetInfo
            if (!assetData || Object.keys(assetData).length <= 1) {
                assetData = selectedAsset[0]
            }

            // Get the equipment ID from various possible sources
            const equipmentId =
                assetData?.external_id ||
                assetData?.salesforceID ||
                assetData?.id ||
                assetData?.customId ||
                selectedAsset[0]?.salesforceID ||
                selectedAsset[0]?.id ||
                selectedAsset[0]?.customId

            if (!equipmentId) {
                console.error(
                    'Could not determine equipment ID from available data',
                )
            }

            const details = {
                ...data,
                userId: user?.id,
                photoId: photosIds[0] || null,
                imageRetryAttempted,
                equipmentSFId: equipmentId,
                equipmentId: equipmentId,
                assetId: equipmentId,
                assetSalesforceId: assetData?.salesforceID || equipmentId,
                assetName:
                    assetData?.name ||
                    assetData?.customerAlias ||
                    selectedAsset[0]?.name ||
                    'Unknown Asset',
                assetCustomId:
                    assetData?.customId || selectedAsset[0]?.customId,
                // Add more fallbacks for asset data
                serialNumber:
                    assetData?.serialLotNumber ||
                    selectedAsset[0]?.serialLotNumber,
                make: assetData?.manufacturer || selectedAsset[0]?.manufacturer,
                model:
                    assetData?.model || selectedAsset[0]?.model || data.model,
            }

            if (isOffline || assetData.isCreatedOffline) {
                // We need to location selected by the user for showing the info on the details while being not synced
                const locationSelected = user?.locationPreferences.find(
                    item => item.value == data.shipToAccountId,
                )
                Object.assign(details, {
                    customerAlias:
                        assetData?.customerAlias ||
                        assetData?.name ||
                        selectedAsset[0]?.customerAlias ||
                        selectedAsset[0]?.name,
                    requestedByName: `${user?.first_name ??
                        ''} ${user?.last_name ?? ''}`,
                    address: locationSelected?.address,
                    locationName: locationSelected?.label,
                    showType: 'Parts',
                    type: 'Parts',
                    images: await processImagesHelper(images),
                })
            }

            if (isOffline) {
                try {
                    const savedRequest = await savePendingRequest(details)
                    onCreatedOfflineRequestSuccess(savedRequest)
                } catch (error) {
                    setError('Failed to save request for offline processing')
                    changeLoading(false)
                }
                return
            } else {
                handleCreatePartsRequest(
                    details,
                    onCreatedPartRequestSuccess,
                    onCreatedPartRequestError,
                )
            }
        },
        [
            assetInfo,
            photosIds,
            user,
            isOffline,
            images,
            selectedAsset,
            imageRetryAttempted,
        ],
    )

    const handleCancel = () => {
        removeStepsAndRequests()
        history?.push(AppRoutes?.createRequestStepper())
    }

    const handleContinueClick = () => {
        const activeStep = steps?.find(f => f.active === true)

        const assetName = getAssetName()

        // If we have an asset selected but somehow no active step or wrong step,
        // force update the steps to show Request Details as active
        if (
            selectedAsset.length > 0 &&
            (!activeStep || activeStep?.name === 'Select Asset')
        ) {
            const updatedSteps = steps.map(step => {
                if (step.name === 'Request Details') {
                    return { ...step, active: true, finish: false }
                } else if (step.name === 'Select Asset') {
                    return {
                        ...step,
                        active: false,
                        finish: true,
                        description: assetName,
                    }
                } else if (step.name === 'Check Duplicates') {
                    return { ...step, active: false, finish: true }
                }
                return step
            })
            setSteps(updatedSteps)
            return
        }

        onNextStep()
    }

    // Track form submission state
    const [formSubmitting, setFormSubmitting] = useState(false)

    // Function to be passed to the form to update parent's submission state
    const trackFormSubmission = isSubmitting => {
        setFormSubmitting(isSubmitting)
    }

    const renderFooter = () => {
        const activeStep = steps?.find(f => f.active === true)
        const isRequestDetailsActive =
            selectedAsset.length > 0 &&
            (activeStep?.name === 'Request Details' || !activeStep)

        // Request errors should not force users to edit fields before retrying.
        const buttonDisabled = isRequestDetailsActive
            ? !isValidForm || formSubmitting
            : error != null

        return (
            <div className="w-full px-6 py-3.5 sticky bottom-0 left-0 bg-white border-t border-grayLines flex items-center gap-2 justify-center">
                <WarningCard error={error} />
                <Button
                    onClick={() => handleCancel()}
                    variant="tertiary"
                    text="Cancel"
                    type="button"
                    disabled={formSubmitting}
                />
                <Button
                    type={isRequestDetailsActive ? 'submit' : 'button'}
                    key="submit"
                    variant="secondary"
                    disabled={buttonDisabled}
                    form={isRequestDetailsActive ? 'create-parts-request' : ''}
                    className="lg:w-auto w-full lg:flex-grow-0 flex-grow"
                    onClick={e => {
                        // If we're on the Request Details step, let the form handle submission naturally
                        if (!isRequestDetailsActive) {
                            // Only prevent default and handle custom logic for non-form steps
                            e.preventDefault()
                            handleContinueClick()
                        }
                        // Otherwise, let the natural form submission happen via the form="create-parts-request" attribute
                    }}
                    text={
                        isRequestDetailsActive
                            ? formSubmitting
                                ? 'Submitting...'
                                : 'Submit Request'
                            : 'Continue'
                    }
                />
            </div>
        )
    }

    const renderSelectAssetContent = () => {
        if (isMobile || isTablet) {
            return (
                <FleetTable
                    onSelectItems={setSelectedAsset}
                    selectedItems={selectedAsset}
                    selectable={true}
                    isInCard
                    title="Asset for Repair"
                    subtitle="Select the asset by clicking the radio button"
                    isCreateRequest
                />
            )
        }

        return (
            <CardInfo
                isTableContent
                title="Asset for Repair"
                subTitle="Select the asset by clicking the radio button"
                content={
                    <FleetTable
                        onSelectItems={setSelectedAsset}
                        selectedItems={selectedAsset}
                        selectable={true}
                        isInCard
                        isCreateRequest
                    />
                }
            />
        )
    }

    const renderCheckDuplicates = () => {
        return (
            <CardInfo
                isTableContent
                title="Check Duplicate Requests"
                subTitle="Make sure there are no similar requests opened for this asset"
                content={
                    <RequestTable
                        getAllRequests={false}
                        data={filteredOpenRequests}
                        onFilter={onFilterOpenRequests}
                        isInCard
                    />
                }
            />
        )
    }

    const renderContent = () => {
        const activeStep = steps?.find(f => f.active === true)
        if (activeStep) {
            const stepName = activeStep.name

            switch (stepName) {
                case 'Select Asset':
                    if (asset) {
                        return null
                    } else {
                        return renderSelectAssetContent()
                    }
                case 'Check Duplicates':
                    return renderCheckDuplicates()
                case 'Request Details':
                    return (
                        <CreatePartsQuoteForm
                            images={images}
                            photosIds={photosIds}
                            setImages={setImages}
                            setPhotosIds={setPhotosIds}
                            onSubmitForm={onCreateRequest}
                            formRef={formRef}
                            asset={selectedAsset[0]}
                            setError={setError}
                            setIsValidForm={setIsValidForm}
                            successfulSubmit={successfulSubmit}
                            setSuccessfulSubmit={setSuccessfulSubmit}
                            resetTouch={resetTouch}
                            setResetTouch={setResetTouch}
                            trackSubmission={trackFormSubmission}
                            error={error}
                        />
                    )
                default:
                    return null
            }
        }
        if (selectedAsset.length > 0) {
            return (
                <CreatePartsQuoteForm
                    images={images}
                    photosIds={photosIds}
                    setImages={setImages}
                    setPhotosIds={setPhotosIds}
                    onSubmitForm={onCreateRequest}
                    formRef={formRef}
                    asset={selectedAsset[0]}
                    setError={setError}
                    setIsValidForm={setIsValidForm}
                    successfulSubmit={successfulSubmit}
                    setSuccessfulSubmit={setSuccessfulSubmit}
                    resetTouch={resetTouch}
                    setResetTouch={setResetTouch}
                    trackSubmission={trackFormSubmission}
                    error={error}
                />
            )
        }
        return renderSelectAssetContent()
    }

    // Ensure there is always exactly one active step. Only corrects missing
    // active steps – never overrides a valid stored step (e.g. "Select Asset"
    // on refresh).
    useEffect(() => {
        const validActiveStep = steps?.find(step => step.active === true)
        if (!validActiveStep) {
            const correctedSteps = steps.map(step => {
                if (step.name === 'Select Asset') {
                    return { ...step, active: true, finish: false }
                }
                return { ...step, active: false, finish: false }
            })
            setSteps(correctedSteps)
        }
    }, []) // Run once on component mount

    return (
        <ContentLayout>
            <MainHeading
                isBlack={isMobile || isTablet}
                title="Create Parts Quote Request"
                content={
                    <TimeLineSteps
                        steps={steps}
                        key="Stepper"
                        isStepper
                        isAbsolute={false}
                        isDark
                        showDescription={false}
                    />
                }
                hasGoBack
                steps={steps}
                setSteps={setSteps}
                listOfRequests={listOfRequests}
                setSelectedAsset={setSelectedAsset}
            />
            {/* Content */}
            <div className="w-full flex-grow h-full max-h-full overflow-auto lg:px-6 lg:py-8">
                {renderContent()}
            </div>
            {renderFooter()}
        </ContentLayout>
    )
}

CreatePartsRequestFlow.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func,
        goBack: PropTypes.func,
    }),
}

export default CreatePartsRequestFlow
